import { Box, CardTabs, useUpdateQueryParams } from 'vopay';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Title } from '#shared/components/UI';
import AccountAndBillingProvider from './AccountAndBilling.provider';
import BillingInvoices from './BillingInvoices/BillingInvoices';
import BillingPaymentMethods from './BillingPaymentMethods';
import { SECOND_TAB_VARIANTS } from './constants';
import { AccountAndBillingHostProps } from './types';

type AccountBillingType = 'paymentMethods' | 'invoices';

type QueryParams = {
  type: string;
  currency: string;
  accountBillingType: AccountBillingType;
  searchTerm: string;
};

// The tab is arbitrary URL text, so it is narrowed rather than asserted — asserted,
// `?accountBillingType=banana` types as an AccountBillingType and renders neither tab's content.
function isAccountBillingType(value: string | null): value is AccountBillingType {
  return value === 'paymentMethods' || value === 'invoices';
}

// The feature root: takes the host props and mounts the feature-local provider, so
// every internal component's useAccountAndBilling() resolves. Hosts render it from the
// shared/accountAndBilling entry: <AccountAndBillingPage {...useBillingHostProps()} />.
export default function AccountAndBillingPage({
  viewer,
  account,
  userPermissions,
  billingApi,
  secondTab = 'invoiceAndStatement',
}: AccountAndBillingHostProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountAndBillingPage' });
  const location = useLocation();
  const { tabLabel } = SECOND_TAB_VARIANTS[secondTab];

  // QueryParams stays whole even though only the tab is read here: useUpdateQueryParams<QueryParams>
  // needs the full shape to know which keys handleTabChange is allowed to clear.
  const activeTab: AccountBillingType = useMemo(() => {
    const value = new URLSearchParams(location.search).get('accountBillingType');

    return isAccountBillingType(value) ? value : 'paymentMethods';
  }, [location.search]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  function handleTabChange(value: string) {
    if (!isAccountBillingType(value)) return;

    updateQueryParams({
      accountBillingType: value,
      searchTerm: undefined,
      type: undefined,
      currency: undefined,
    });
  }

  return (
    <AccountAndBillingProvider
      viewer={viewer}
      account={account}
      userPermissions={userPermissions}
      billingApi={billingApi}
      secondTab={secondTab}
    >
      <Box jc="sb" flx="col" gapSize="36px">
        <Title>{t('title', { defaultValue: 'Account & Billing' })}</Title>

        <Box flx="col" width="100%">
          <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
            <CardTabs.List aria-label={t('tabsAriaLabel', { defaultValue: 'Account and Billing' })}>
              <CardTabs.Trigger value="paymentMethods">
                {t('Tabs.paymentMethods', { defaultValue: 'Payment Methods' })}
              </CardTabs.Trigger>

              <CardTabs.Trigger value="invoices">
                {t(tabLabel.key, { defaultValue: tabLabel.defaultValue })}
              </CardTabs.Trigger>
            </CardTabs.List>

            <CardTabs.Content value="paymentMethods">
              <BillingPaymentMethods />
            </CardTabs.Content>

            <CardTabs.Content value="invoices">
              <BillingInvoices />
            </CardTabs.Content>
          </CardTabs.Root>
        </Box>
      </Box>
    </AccountAndBillingProvider>
  );
}
